import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X, ShoppingBag, Wand2, Calendar } from "lucide-react";
import { useCart } from "../lib/cart";

// Nav hierarchy — the digital experiences (generator + planner) are the stars.
// Kits are the "plug-and-play or premium" path for parents who'd rather buy
// than design, so they sit further down the list.
const STAR_LINKS = [
  { path: "/create", label: "Make a Buddy", icon: Wand2, color: "#F26A34" },
  { path: "/planner", label: "Meal Planner", icon: Calendar, color: "#7CC5D4" },
];
const ANCHOR_LINKS = [];
const ROUTE_LINKS = [
  { path: "/home#recipes", label: "Recipes" },
  { path: "/journal", label: "Journal" },
  { path: "/shop", label: "Kits" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { itemCount } = useCart();

  const scrollTo = (id) => {
    setOpen(false);
    if (window.location.pathname !== "/home") {
      navigate(`/home#${id}`);
      return;
    }
    // Defer scroll until after the mobile menu collapses, otherwise the
    // smooth-scroll calculates against the taller (open-menu) layout
    // and undershoots by the menu's height once it closes.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
      });
    });
  };

  return (
    <header data-testid="navbar" className="sticky top-0 z-40 backdrop-blur-md bg-pb-brand-purple/95 border-b border-white/10 shadow-[0_4px_24px_-12px_rgba(0,0,0,0.4)]">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-3.5 flex items-center justify-between gap-4">
        <a href="/home" data-testid="link-logo" className="flex items-center group flex-shrink-0">
          <img
            src="/brand/logo-wordmark.png"
            alt="Plate Buddies"
            className="h-9 sm:h-11 w-auto group-hover:scale-105 transition-transform"
          />
        </a>

        <nav className="hidden md:flex items-center gap-3 text-sm font-extrabold text-white/85">
          {/* Star links — the digital experiences that make Plate Buddies, Plate Buddies. */}
          {STAR_LINKS.map((l) => (
            <Link
              key={l.path}
              to={l.path}
              data-testid={`nav-star-${l.path.replace("/", "")}`}
              className="group inline-flex items-center gap-2 rounded-full px-4 py-2 hover:bg-white/10 transition"
            >
              <span
                className="h-6 w-6 rounded-lg flex items-center justify-center text-white shadow-sm group-hover:scale-110 transition-transform"
                style={{ background: l.color }}
              >
                <l.icon className="h-3.5 w-3.5" />
              </span>
              <span className="group-hover:text-pb-brand-orange transition">{l.label}</span>
            </Link>
          ))}

          <span className="h-5 w-px bg-white/20 mx-1" aria-hidden="true" />

          {/* Secondary links */}
          {ANCHOR_LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`link-${l.id}`}
              onClick={() => scrollTo(l.id)}
              className="px-2 hover:text-pb-brand-orange transition"
            >
              {l.label}
            </button>
          ))}
          {ROUTE_LINKS.map((l) => (
            <Link
              key={l.path}
              to={l.path}
              data-testid={`link-${l.path.replace("/", "")}`}
              className="px-2 hover:text-pb-brand-orange transition"
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-3 flex-shrink-0">
          <Link
            to="/cart"
            data-testid="link-cart"
            className="relative inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-white shadow-[0_4px_12px_rgba(0,0,0,0.18)] border border-white/40 hover:bg-pb-sun transition"
            aria-label="Cart"
          >
            <ShoppingBag className="h-4 w-4 text-pb-brand-ink" />
            {itemCount > 0 && (
              <span
                data-testid="cart-badge"
                className="absolute -top-1.5 -right-1.5 bg-pb-brand-orange text-white text-[10px] font-extrabold rounded-full h-5 min-w-5 px-1 flex items-center justify-center shadow"
              >
                {itemCount}
              </span>
            )}
          </Link>
          <button
            data-testid="btn-menu-toggle"
            onClick={() => setOpen((o) => !o)}
            className="md:hidden rounded-2xl p-2.5 bg-white shadow border border-white/40 text-pb-brand-ink"
            aria-label="Menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>
      {open && (
        <div className="md:hidden bg-pb-brand-purple border-t border-white/10 px-6 py-4 space-y-1.5">
          {/* Star links first, with icon badges, so mobile mirrors the desktop hierarchy */}
          {STAR_LINKS.map((l) => (
            <Link
              key={l.path}
              to={l.path}
              onClick={() => setOpen(false)}
              data-testid={`mobile-star-${l.path.replace("/", "")}`}
              className="flex items-center gap-3 py-2.5 font-extrabold text-white"
            >
              <span
                className="h-8 w-8 rounded-xl flex items-center justify-center text-white shadow-sm"
                style={{ background: l.color }}
              >
                <l.icon className="h-4 w-4" />
              </span>
              {l.label}
            </Link>
          ))}
          <div className="pt-2 mt-2 border-t border-white/15 space-y-1">
            {ANCHOR_LINKS.map((l) => (
              <button
                key={l.id}
                onClick={() => scrollTo(l.id)}
                className="block w-full text-left font-extrabold text-white/90 py-2"
                data-testid={`mobile-link-${l.id}`}
              >
                {l.label}
              </button>
            ))}
            {ROUTE_LINKS.map((l) => (
              <Link
                key={l.path}
                to={l.path}
                onClick={() => setOpen(false)}
                data-testid={`mobile-link-${l.path.replace("/", "")}`}
                className="block w-full text-left font-extrabold text-white/90 py-2"
              >
                {l.label}
              </Link>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}
